// src/hooks/useSupabaseReimbursements.js
// Supabase CRUD hook for the reimbursements table.
// Follows the field-locked pattern from useSupabase.js.

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError } from './useSupabase';

// Session guard (same pattern as useSupabase.js)
const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session || !session.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

// Map DB row (snake_case) → JS object (camelCase)
const mapRow = (r) => ({
  id:               r.id,
  projectId:        r.project_id,
  userId:           r.user_id,
  expenseDate:      r.expense_date,
  vendor:           r.vendor || '',
  description:      r.description || '',
  category:         r.category || 'Miscellaneous',
  amount:           r.amount ?? 0,
  currency:         r.currency || 'INR',
  amountInInr:      r.amount_in_inr ?? r.amount ?? 0,
  fxRateToInr:      r.fx_rate_to_inr ?? 1,
  officeLocation:   r.office_location || 'IN',
  status:           r.status || 'pending',
  reimbursedDate:   r.reimbursed_date || null,
  reimbursedMethod: r.reimbursed_method || null,
  reimbursedBy:     r.reimbursed_by || null,
  voucherId:        r.voucher_id || null,
  voucherDate:      r.voucher_date || null,
  voucherStatus:    r.voucher_status || null,
  fileName:         r.file_name || null,
  fileDropboxPath:  r.file_dropbox_path || null,
  fileDropboxUrl:   r.file_dropbox_url || null,
  linkedExpenseId:  r.linked_expense_id || null,
  clientBillable:   r.client_billable ?? false,
  isOfficeExpense:  r.is_office_expense ?? false,
  billingStatus:    r.billing_status || null,
  importBatchId:    r.import_batch_id || null,
  createdBy:        r.created_by || null,
  createdAt:        r.created_at,
  updatedAt:        r.updated_at,
});

export default function useSupabaseReimbursements({ enabled = true } = {}) {
  const [reimbursements, setReimbursements] = useState([]);
  const [loading, setLoading] = useState(enabled);

  // ── FETCH ─────────────────────────────────────────────────────────
  const fetchReimbursements = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('reimbursements')
        .select('*')
        .order('expense_date', { ascending: false });

      if (error) throw error;
      setReimbursements((data || []).map(mapRow));
    } catch (err) {
      console.error('[REIMBURSEMENTS] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) fetchReimbursements(); }, [enabled, fetchReimbursements]);

  // ── CREATE ────────────────────────────────────────────────────────
  const createReimbursement = useCallback(async (d) => {
    try {
      const authUserId = await requireSession();
      const dbData = {
        project_id:       d.projectId || null,
        user_id:          d.userId,
        expense_date:     d.expenseDate || new Date().toISOString().split('T')[0],
        vendor:           d.vendor || null,
        description:      d.description || null,
        category:         d.category || 'Miscellaneous',
        amount:           d.amount,
        currency:         d.currency || 'INR',
        amount_in_inr:    d.amountInInr ?? d.amount,
        fx_rate_to_inr:   d.fxRateToInr ?? 1,
        office_location:  d.officeLocation || 'IN',
        status:           d.status || 'pending',
        file_name:        d.fileName || null,
        file_dropbox_path: d.fileDropboxPath || null,
        file_dropbox_url: d.fileDropboxUrl || null,
        linked_expense_id: d.linkedExpenseId || null,
        client_billable:  d.clientBillable ?? false,
        is_office_expense: d.isOfficeExpense ?? false,
        billing_status:   d.billingStatus || null,
        created_by:       authUserId,
      };

      const { data, error } = await supabase
        .from('reimbursements')
        .insert([dbData])
        .select()
        .single();

      if (error) throw error;
      await fetchReimbursements();
      return { success: true, data: mapRow(data) };
    } catch (err) {
      console.error('[REIMBURSEMENTS] create error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'reimbursement' }) };
    }
  }, [fetchReimbursements]);

  // ── UPDATE ────────────────────────────────────────────────────────
  const updateReimbursement = useCallback(async (id, updates) => {
    try {
      await requireSession();
      const db = {};
      if (updates.projectId !== undefined)       db.project_id = updates.projectId;
      if (updates.userId !== undefined)          db.user_id = updates.userId;
      if (updates.expenseDate !== undefined)     db.expense_date = updates.expenseDate;
      if (updates.vendor !== undefined)          db.vendor = updates.vendor;
      if (updates.description !== undefined)     db.description = updates.description;
      if (updates.category !== undefined)        db.category = updates.category;
      if (updates.amount !== undefined)          db.amount = updates.amount;
      if (updates.currency !== undefined)        db.currency = updates.currency;
      if (updates.amountInInr !== undefined)     db.amount_in_inr = updates.amountInInr;
      if (updates.fxRateToInr !== undefined)     db.fx_rate_to_inr = updates.fxRateToInr;
      if (updates.officeLocation !== undefined)  db.office_location = updates.officeLocation;
      if (updates.status !== undefined)          db.status = updates.status;
      if (updates.reimbursedDate !== undefined)  db.reimbursed_date = updates.reimbursedDate;
      if (updates.reimbursedMethod !== undefined) db.reimbursed_method = updates.reimbursedMethod;
      if (updates.reimbursedBy !== undefined)    db.reimbursed_by = updates.reimbursedBy;
      if (updates.voucherId !== undefined)       db.voucher_id = updates.voucherId;
      if (updates.voucherDate !== undefined)     db.voucher_date = updates.voucherDate;
      if (updates.voucherStatus !== undefined)   db.voucher_status = updates.voucherStatus;
      if (updates.fileName !== undefined)        db.file_name = updates.fileName;
      if (updates.fileDropboxPath !== undefined) db.file_dropbox_path = updates.fileDropboxPath;
      if (updates.fileDropboxUrl !== undefined)  db.file_dropbox_url = updates.fileDropboxUrl;
      if (updates.linkedExpenseId !== undefined) db.linked_expense_id = updates.linkedExpenseId;
      if (updates.clientBillable !== undefined)  db.client_billable = updates.clientBillable;
      if (updates.isOfficeExpense !== undefined) db.is_office_expense = updates.isOfficeExpense;
      if (updates.billingStatus !== undefined)   db.billing_status = updates.billingStatus;
      db.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('reimbursements')
        .update(db)
        .eq('id', id);

      if (error) throw error;
      await fetchReimbursements();
      return { success: true };
    } catch (err) {
      console.error('[REIMBURSEMENTS] update error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'reimbursement' }) };
    }
  }, [fetchReimbursements]);

  // ── DELETE ────────────────────────────────────────────────────────
  const deleteReimbursement = useCallback(async (id) => {
    try {
      await requireSession();

      // Unlink any expenses that reference this reimbursement (FK: expenses.linked_reimbursement_id)
      const { error: unlinkError } = await supabase
        .from('expenses')
        .update({ linked_reimbursement_id: null })
        .eq('linked_reimbursement_id', id);
      if (unlinkError) console.warn('[REIMBURSEMENTS] Could not unlink expenses:', unlinkError.message);

      const { error } = await supabase.from('reimbursements').delete().eq('id', id);
      if (error) throw error;
      await fetchReimbursements();
      return { success: true };
    } catch (err) {
      console.error('[REIMBURSEMENTS] delete error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'reimbursement' }) };
    }
  }, [fetchReimbursements]);

  // ── BULK CREATE (for statement imports — skip approval) ──────────
  // Creates multiple reimbursement records at once with status 'approved',
  // bypassing the normal pending → approved approval pipeline.
  const bulkCreateReimbursements = useCallback(async (rows) => {
    const results = { successCount: 0, errors: [] };
    if (!rows.length) return results;
    try {
      const authUserId = await requireSession();
      const dbRows = rows.map(r => ({
        project_id:        r.projectId || null,
        user_id:           r.userId || null,
        expense_date:      r.expenseDate || new Date().toISOString().split('T')[0],
        vendor:            r.vendor || null,
        description:       r.description || null,
        category:          r.category || 'Miscellaneous',
        amount:            r.amount,
        currency:          r.currency || 'INR',
        amount_in_inr:     r.amountInInr ?? r.amount,
        fx_rate_to_inr:    r.fxRateToInr ?? 1,
        office_location:   r.officeLocation || 'IN',
        status:            'approved',          // skip approval pipeline
        linked_expense_id: r.linkedExpenseId || null,
        client_billable:   r.clientBillable ?? false,
        is_office_expense: r.isOfficeExpense ?? false,
        import_batch_id:   r.importBatchId || null,
        created_by:        authUserId,
      }));

      const { data, error } = await supabase
        .from('reimbursements')
        .insert(dbRows)
        .select();

      if (error) throw error;
      results.successCount = Array.isArray(data) ? data.length : dbRows.length;
      if (results.successCount > 0) await fetchReimbursements();
      return results;
    } catch (err) {
      console.error('[REIMBURSEMENTS] bulkCreate error:', err);
      results.errors.push(formatSupabaseError(err, { action: 'bulk create', entity: 'reimbursements' }));
      return results;
    }
  }, [fetchReimbursements]);

  // ── BATCH VOUCHER ─────────────────────────────────────────────────
  // Group selected reimbursement IDs into a voucher
  const createVoucher = useCallback(async (reimbursementIds, voucherMeta = {}) => {
    try {
      await requireSession();
      const voucherId = voucherMeta.voucherId || `VCH-${Date.now().toString(36).toUpperCase()}`;
      const { error } = await supabase
        .from('reimbursements')
        .update({
          voucher_id:     voucherId,
          voucher_date:   voucherMeta.date || new Date().toISOString().split('T')[0],
          voucher_status: 'draft',
          status:         'voucher',
          updated_at:     new Date().toISOString(),
        })
        .in('id', reimbursementIds);

      if (error) throw error;
      await fetchReimbursements();
      return { success: true, voucherId };
    } catch (err) {
      console.error('[REIMBURSEMENTS] createVoucher error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create voucher for', entity: 'reimbursements' }) };
    }
  }, [fetchReimbursements]);

  // ── MARK VOUCHER STATUS ───────────────────────────────────────────
  const updateVoucherStatus = useCallback(async (voucherId, newStatus) => {
    try {
      await requireSession();
      const updates = { voucher_status: newStatus, updated_at: new Date().toISOString() };
      if (newStatus === 'invoiced') updates.status = 'invoiced';
      if (newStatus === 'paid') updates.status = 'invoiced';

      const { error } = await supabase
        .from('reimbursements')
        .update(updates)
        .eq('voucher_id', voucherId);

      if (error) throw error;
      await fetchReimbursements();
      return { success: true };
    } catch (err) {
      console.error('[REIMBURSEMENTS] updateVoucherStatus error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update voucher status for', entity: 'reimbursements' }) };
    }
  }, [fetchReimbursements]);

  // ── REMOVE ITEM FROM VOUCHER ────────────────────────────────────────
  // Removes a single reimbursement from its voucher (reverts to 'approved' status)
  const removeFromVoucher = useCallback(async (reimbursementId) => {
    try {
      await requireSession();
      const { error } = await supabase
        .from('reimbursements')
        .update({
          voucher_id: null,
          voucher_date: null,
          voucher_status: null,
          status: 'approved',
          updated_at: new Date().toISOString(),
        })
        .eq('id', reimbursementId);

      if (error) throw error;
      await fetchReimbursements();
      return { success: true };
    } catch (err) {
      console.error('[REIMBURSEMENTS] removeFromVoucher error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'remove item from voucher for', entity: 'reimbursement' }) };
    }
  }, [fetchReimbursements]);

  // ── DELETE VOUCHER ──────────────────────────────────────────────────
  // Dissolves a voucher — reverts all its items back to 'approved' status
  const deleteVoucher = useCallback(async (voucherId) => {
    try {
      await requireSession();
      const { error } = await supabase
        .from('reimbursements')
        .update({
          voucher_id: null,
          voucher_date: null,
          voucher_status: null,
          status: 'approved',
          updated_at: new Date().toISOString(),
        })
        .eq('voucher_id', voucherId);

      if (error) throw error;
      await fetchReimbursements();
      return { success: true };
    } catch (err) {
      console.error('[REIMBURSEMENTS] deleteVoucher error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete voucher for', entity: 'reimbursements' }) };
    }
  }, [fetchReimbursements]);

  return {
    reimbursements,
    loading,
    createReimbursement,
    bulkCreateReimbursements,
    updateReimbursement,
    deleteReimbursement,
    createVoucher,
    updateVoucherStatus,
    removeFromVoucher,
    deleteVoucher,
    refetch: fetchReimbursements,
  };
}
